import React, { useState, useEffect, useReducer, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  useTheme,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
  CheckCircle as CheckCircleIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { isArray } from "lodash";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_QUICKMESSAGES": {
      const items = action.payload;
      const newItems = [];
      if (isArray(items)) {
        items.forEach((item) => {
          const index = state.findIndex((u) => u.id === item.id);
          if (index !== -1) {
            state[index] = item;
          } else {
            newItems.push(item);
          }
        });
      }
      return [...state, ...newItems];
    }
    case "UPDATE_QUICKMESSAGES": {
      const item = action.payload;
      const index = state.findIndex((u) => u.id === item.id);
      if (index !== -1) {
        state[index] = item;
        return [...state];
      }
      return [item, ...state];
    }
    case "DELETE_QUICKMESSAGE": {
      const id = action.payload;
      const idx = state.findIndex((u) => u.id === id);
      if (idx !== -1) state.splice(idx, 1);
      return [...state];
    }
    case "RESET":
      return [];
    default:
      return state;
  }
};

export default function Quickemessages() {
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);
  const theme = useTheme();

  const menus = quickemessages.filter((i) => i.isCategory);
  const mensagens = quickemessages.filter((i) => !i.isCategory);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(() => {
      fetchQuickemessages();
    }, 500);
    return () => clearTimeout(delay);
    // eslint-disable-next-line
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const handleSocket = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-quickemessage`, handleSocket);
    return () => {
      socket.off(`company-${companyId}-quickemessage`, handleSocket);
    };
  }, [socket, user.companyId]);

  const fetchQuickemessages = async () => {
    try {
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleChangeTab = (_, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (isCategory) => {
    setSelectedQuickemessage({ isCategory });
    setQuickMessageDialogOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedQuickemessage(item);
    setQuickMessageDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/quick-messages/${id}`);
      toast.success(i18n.t("quickemessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setConfirmModalOpen(false);
    setPageNumber(1);
    dispatch({ type: "RESET" });
    fetchQuickemessages();
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - 100 < clientHeight) {
      setPageNumber((prev) => prev + 1);
    }
  };

  const renderTable = (data, columnsCount, isMenu) => (
    <TableContainer onScroll={handleScroll} style={{ maxHeight: 500, overflow: "auto", marginTop: 16 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell align="center">Atalho</TableCell>
            {!isMenu && <TableCell align="center">Prévia</TableCell>}
            {!isMenu && <TableCell align="center">Anexo</TableCell>}
            {!isMenu && <TableCell align="center">Editável</TableCell>}
            <TableCell align="center">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <TableRow key={item.id}>
              <TableCell align="center">{item.shortcode}</TableCell>
              {!isMenu && (
                <TableCell align="center">
                  {item.message ? item.message.substring(0, 30) + "..." : "Sem mensagem"}
                </TableCell>
              )}
              {!isMenu && (
                <TableCell align="center">
                  {item.mediaName ? (
                    item.mediaUrl ? (
                      <a href={item.mediaUrl} target="_blank" rel="noopener noreferrer">
                        {item.mediaName}
                      </a>
                    ) : (
                      item.mediaName
                    )
                  ) : (
                    i18n.t("quickMessages.noAttachment")
                  )}
                </TableCell>
              )}
              {!isMenu && (
                <TableCell align="center">
                  {item.geral && <CheckCircleIcon color="secondary" />}
                </TableCell>
              )}
              <TableCell align="center">
                <IconButton size="small" onClick={() => handleEdit(item)}>
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    setDeletingQuickemessage(item);
                    setConfirmModalOpen(true);
                  }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {loading && (
            <TableRow>
              <TableCell colSpan={columnsCount}>
                <TableRowSkeleton columns={columnsCount} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingQuickemessage &&
          `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${
            deletingQuickemessage.shortcode
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deletingQuickemessage?.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickemessages();
        }}
        open={quickemessageModalOpen}
        onClose={() => {
          setQuickMessageDialogOpen(false);
          dispatch({ type: "RESET" });
          setPageNumber(1);
          fetchQuickemessages();
        }}
        quickemessageId={selectedQuickemessage?.id}
        defaultCategory={selectedQuickemessage?.isCategory}
      />

      <Box style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <Box style={{ padding: 16 }}>
          <Title>Respostas Rápidas (Layout Renovado)</Title>
          <MainHeader>
            <Box style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
              <Button variant="contained" onClick={() => handleOpenDialog(true)}>
                ADICIONAR MENU
              </Button>
              <Button variant="contained" onClick={() => handleOpenDialog(false)}>
                ADICIONAR MENSAGEM
              </Button>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Pesquisar..."
                value={searchParam}
                onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="secondary" />
                    </InputAdornment>
                  ),
                }}
                style={{ minWidth: 220 }}
              />
            </Box>
          </MainHeader>
        </Box>

        <Box style={{ paddingLeft: 16, paddingRight: 16 }}>
          <Tabs value={activeTab} onChange={handleChangeTab} textColor="primary" indicatorColor="primary">
            <Tab label={`Menus (${menus.length})`} />
            <Tab label={`Mensagens (${mensagens.length})`} />
          </Tabs>
        </Box>

        <Box style={{ flex: 1, paddingLeft: 16, paddingRight: 16, paddingBottom: 16 }}>
          {activeTab === 0 && (
            <Box>
              {renderTable(menus, 2, true)}
            </Box>
          )}
          {activeTab === 1 && (
            <Box>
              {renderTable(mensagens, 5, false)}
            </Box>
          )}
        </Box>
      </Box>
    </MainContainer>
  );
}
